<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { useRouter }  from '#imports'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { grades1to4, grades5to7, gradesAdult } from '~/utils/gradeSorter'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { useStaticProduct } from '~/composables/useStaticProduct'
import { useHeaderChildrenQuery } from '~/composables/useHeader'
import { useAssignTask } from '~/composables/useAssignTask'
import { EXAM_TAG } from '~/composables/useAssignTags'
import useContentApi from '~/api/contentApi'
import ArticleNavigationCard from '~/components/article/ArticleNavigationCard.vue'

const { item } = defineProps<{
  item: BaseItem
}>()

const contentTypes = [
  ContentType.Article,
  ContentType.Audio,
  ContentType.Chatbot,
  ContentType.CodeEditor,
  ContentType.FlytTask,
  ContentType.FormWriter,
  ContentType.ModelText,
  ContentType.Video,
  ContentType.Conversation,
]

const router = useRouter()
const { findContents } = useContentApi()
const { Terminprover } = useStaticProduct()
const { isAuthenticated } = storeToRefs(useAuthStore())

const { tasks } = useAssignTask({ tags: computed(() => (
  isAuthenticated.value
  && Terminprover.aunivers.pathString
  && item.pathString.startsWith(Terminprover.aunivers.pathString))
  ? [EXAM_TAG]
  : undefined) })

const taskId = computed(() => router.currentRoute.value.params.taskId)
const task = computed(() => tasks.value?.find(task => task.taskId === taskId.value))
const taskLocationIds = computed(() => task.value?.taskLocationIds ?? [])
const hasTaskLocationIds = computed(() => !!taskLocationIds.value.length)
const { getProductFromResource } = useProductStore()

const { data: unfilteredSiblings } = useHeaderChildrenQuery(
  computed(() => isAuthenticated.value ? item.parentLocationId : undefined),
  {
    sortField: computed(() => item.parentLocationSortField),
    sortOrder: computed(() => item.parentLocationSortOrder),
  }
)

const { data: unfilteredTaskSiblings, isEnabled: isTaskSiblingsEnabled } = useQuery({
  staleTime: Infinity,
  enabled: computed(() => isAuthenticated.value && taskLocationIds.value.length > 0),
  queryKey: computed(() => ['siblings', 'ids', task.value?.taskLocationIds]),
  queryFn: () => findContents({
    locationIdCriterion: taskLocationIds.value,
  }, taskLocationIds.value.length),
  select: (data) => {
    const ids = task.value?.taskLocationIds ?? []
    const byId = new Map(data.map(d => [d.locationId, d]))
    return ids.map(id => byId.get(id)).filter(Boolean)
  },
})

const siblings = computed(() =>
  (isTaskSiblingsEnabled.value
    ? unfilteredTaskSiblings.value
    : unfilteredSiblings.value)
    ?.filter(value => value && contentTypes.includes(value.contentTypeIdentifier)) ?? []
)

const isFavoriteNavigation = computed(() => !!router.currentRoute.value.params?.listId)
const isOutsideGradeRange = computed(() => getProductFromResource(item)?.grades.every(grade => grades1to4.concat(grades5to7, gradesAdult).includes(grade)))

const index = computed(() => Number(siblings?.value?.findIndex(sibling => sibling?.locationId === item.locationId)))
const previous = computed(() => siblings?.value?.[index.value - 1])
const next = computed(() => siblings?.value?.[index.value + 1])
</script>

<template>
  <div
    v-if="!isFavoriteNavigation && !isOutsideGradeRange && (previous || next)"
    class="sticky top-[100vh] grid grow grid-cols-3 border-t-2 border-t-(--theme-60) bg-(--theme-10) text-(--theme-60)"
  >
    <ArticleNavigationCard
      :item="previous"
      :has-task-location-ids
      direction="previous"
    />
    <div class="self-center overflow-hidden p-6 text-center text-lg font-semibold overflow-ellipsis whitespace-nowrap md:text-xl">
      <span class="hidden sm:inline">
        {{ item.title }}
      </span>
    </div>
    <ArticleNavigationCard
      :item="next"
      :has-task-location-ids
      direction="next"
    />
  </div>
</template>
